import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useState, useEffect, useCallback } from "react";
// import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Building, Plus, Edit2, Trash2, ShieldAlert, Check } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import { PageHeader, SearchInput, EmptyState, DataTable, AlertBanner } from "../../../components/ui";
import { customerService } from "../../../services/customerService";
import { CRM_ONBOARDING_ADD_PATH, CRM_ONBOARDING_EDIT_PATH, CRM_ONBOARDING_VIEW_PATH } from "../routes";

export function CustomerListPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [customerCount, setCustomerCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [toast, setToast] = useState(null);

  // Search, Sort, Filter, and Pagination State
  const [searchQuery, setSearchQuery] = useState("");
  const [accountTypeFilter, setAccountTypeFilter] = useState("");
  const [accountStatusFilter, setAccountStatusFilter] = useState("");
  const [accountTypeOptions, setAccountTypeOptions] = useState([]);
  const [accountStatusOptions, setAccountStatusOptions] = useState([]);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
  const [sortBy] = useState("customerId");
  const [sortDir] = useState("ASC");
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  // Permission Gating State
  const [permissions] = useState({
    manageCustomerData: true,
    createNewCustomer: true
  });

  // Modal / Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Fetch count dynamically
  const loadCount = useCallback(async () => {
    try {
      const count = await customerService.getCustomerCount();
      setCustomerCount(count);
    } catch (err) {
      console.error("Failed to load customer count:", err);
    }
  }, []);

  // Load Account Type / Account Status filter options from CRM API (OpenAPI schema)
  useEffect(() => {
    let active = true;
    (async () => {
      setFilterOptionsLoading(true);
      try {
        const opts = await customerService.getCustomerInfoDropdownOptions();
        if (!active) return;
        setAccountTypeOptions(opts?.accountType || []);
        setAccountStatusOptions(opts?.accountStatusFilter || opts?.accountStatus || []);
      } catch (err) {
        console.error("Failed to load account type/status filter options:", err);
      } finally {
        if (active) setFilterOptionsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Fetch customers list — uses search/{accountType} and search/status when filters are set
  const loadCustomers = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      let responseData;
      const apiPage = currentPage - 1;
      const apiSize = 10;
      const hasSearch = Boolean(searchQuery.trim());
      const hasType = Boolean(accountTypeFilter);
      const hasStatus = Boolean(accountStatusFilter);

      const normalizeFilterKey = (value) => {
        const key = String(value || "")
          .trim()
          .toLowerCase()
          .replace(/[\s-]/g, "_");
        if (key === "on_hold" || key === "onhold" || key === "hold") return "onhold";
        return key;
      };

      const matchesType = (cust) => {
        if (!hasType) return true;
        return (
          normalizeFilterKey(cust.accountType || cust.account_type) ===
          normalizeFilterKey(accountTypeFilter)
        );
      };

      const matchesStatus = (cust) => {
        if (!hasStatus) return true;
        return (
          normalizeFilterKey(cust.accountStatus || cust.account_status) ===
          normalizeFilterKey(accountStatusFilter)
        );
      };

      if (hasSearch) {
        responseData = await customerService.searchByName(
          searchQuery.trim(),
          apiPage,
          apiSize,
          "companyName",
          sortDir
        );
        if (hasType || hasStatus) {
          const filtered = (responseData.customers || []).filter(
            (c) => matchesType(c) && matchesStatus(c)
          );
          responseData = {
            customers: filtered,
            totalPages: 1,
            totalElements: filtered.length
          };
        }
      } else if (hasType && hasStatus) {
        // Prefer account-type search API, then client-filter status
        responseData = await customerService.searchByAccountType(
          accountTypeFilter,
          apiPage,
          apiSize,
          "companyName",
          sortDir
        );
        const filtered = (responseData.customers || []).filter(matchesStatus);
        responseData = {
          customers: filtered,
          totalPages: responseData.totalPages || 1,
          totalElements: filtered.length
        };
      } else if (hasType) {
        // GET /customer-info/search/{accountType}?accountType=...
        responseData = await customerService.searchByAccountType(
          accountTypeFilter,
          apiPage,
          apiSize,
          "companyName",
          sortDir
        );
      } else if (hasStatus) {
        // GET /customer-info/search/status?accountStatus=...
        responseData = await customerService.searchByAccountStatus(
          accountStatusFilter,
          apiPage,
          apiSize,
          "companyName",
          sortDir
        );
      } else {
        responseData = await customerService.getCustomers(
          apiPage,
          apiSize,
          sortBy,
          sortDir
        );
      }

      const list = responseData.customers || [];
      setCustomers(list);
      setTotalPages(responseData.totalPages || 1);
      setTotalElements(responseData.totalElements || list.length);
      await loadCount();
    } catch (err) {
      console.error("Error loading customers:", err);
      setErrorMsg("Failed to load customers from server.");
    } finally {
      setLoading(false);
    }
  }, [searchQuery, accountTypeFilter, accountStatusFilter, currentPage, sortBy, sortDir, loadCount]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      loadCustomers();
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [loadCustomers]);

  const hasActiveFilters = Boolean(searchQuery.trim() || accountTypeFilter || accountStatusFilter);

  const clearAllFilters = () => {
    setSearchQuery("");
    setAccountTypeFilter("");
    setAccountStatusFilter("");
    setCurrentPage(1);
  };

  const handleStartDelete = (cust) => {
    if (!permissions.manageCustomerData) return;
    const targetId = cust.id || cust.customerId;
    const name = cust.companyName || cust.company_name || `Customer #${targetId}`;
    setDeleteError(null);
    setDeleteTarget({
      id: targetId,
      name
    });
  };

  const handleExecuteDelete = async () => {
    if (!deleteTarget || deleteLoading) return;
    setDeleteLoading(true);
    setDeleteError(null);

    try {
      await customerService.deleteCustomer(deleteTarget.id);
      setDeleteTarget(null);
      setToast({ message: `Customer "${deleteTarget.name}" deleted successfully.`, type: "success" });
      setTimeout(() => setToast(null), 3000);
      await loadCustomers();
    } catch (err) {
      console.error("Delete customer failed:", err);
      const errMsg = err?.data?.message || err?.message || "Failed to delete customer record.";
      setDeleteError(String(errMsg));
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatStatusDisplay = (statusRaw) => {
    const status = String(statusRaw || "").toLowerCase();
    if (status === "active") return { value: "Active", label: "Active", className: "border-emerald-200 text-emerald-700 bg-emerald-50" };
    if (status.includes("hold")) return { value: "On_Hold", label: "On Hold", className: "border-amber-200 text-amber-700 bg-amber-50" };
    if (status === "inactive") return { value: "Inactive", label: "Inactive", className: "border-rose-200 text-rose-700 bg-rose-50" };
    return { value: "Onboarding", label: "Onboarding", className: "border-blue-200 text-blue-700 bg-blue-50" };
  };

  const tableColumns = [
    { label: "Company Name", key: "company" },
    { label: "Industry", key: "industry" },
    { label: "Account Type", key: "type" },
    { label: "Status", key: "status" },
    ...(permissions.manageCustomerData ? [{ label: "Actions", key: "actions", className: "text-right" }] : [])
  ];

  const activePage = Math.min(currentPage, totalPages);

  return (
    <AppLayout
      overlay={
        toast ? (
          <div className="fixed bottom-5 right-5 z-50 animate-in fade-in slide-in-from-bottom-5 duration-300 pointer-events-none">
            <div className="flex items-center gap-2.5 bg-white border border-[var(--color-text-border)]/60 text-foreground px-4 py-3 rounded-2xl shadow-[0_15px_40px_-10px_rgba(30,51,131,0.25)] font-sans text-sm font-medium">
              <div className="h-6 w-6 rounded-full bg-[var(--color-success)]/10 text-[var(--color-success)] grid place-items-center">
                <Check className="h-4 w-4" />
              </div>
              <span>{toast.message}</span>
            </div>
          </div>
        ) : null
      }
    >
      <div className="space-y-6">
        {/* Page Header with Dynamic Count */}
        <div>
          <PageBackLink to="/" />
          <PageHeader
            // title={`Customers (${customerCount})`}
            title={`${i18n.t("common.customer")} (${customerCount})`}
            // subtitle="Manage customer directory accounts, locations, and structural asset inventories."
            action={
              permissions.createNewCustomer && (
                <Button
                  onClick={() => navigate(CRM_ONBOARDING_ADD_PATH)}
                  className="h-11 px-5 rounded-xl bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)]"
                >
                  <Plus className="h-4 w-4" />
                  <Trans i18nKey="formActions.addCustomer" /> 
                  {/* Add Customer */}
                </Button>
              )
            }
          />
        </div>
        {errorMsg && <AlertBanner type="error">{errorMsg}</AlertBanner>}

        {/* Filters Panel */}
        <div className="flex flex-col gap-3 bg-white p-4 rounded-2xl border border-white/60 shadow-[0_4px_20px_-10px_rgba(30,51,131,0.15)]">
          <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
            <div className="w-full lg:flex-1 lg:min-w-[220px]">
              <SearchInput
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                 placeholder={i18n.t("formActions.searchByCompanyName")}
                // placeholder="Search by company name..."
                className="w-full"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full lg:w-auto lg:min-w-[420px]">
              <select
                value={accountTypeFilter}
                onChange={(e) => {
                  setAccountTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                // aria-label="Filter by account type"
                aria-label={i18n.t("formActions.filterByAccountType")}
                disabled={filterOptionsLoading && accountTypeOptions.length === 0}
                className="h-11 w-full rounded-xl border border-[var(--color-text-border)] px-3 text-sm bg-white text-foreground focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10"
              >
                <option value=""><Trans i18nKey="crm.allAccountTypes" /></option>
                {accountTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <select
                value={accountStatusFilter}
                onChange={(e) => {
                  setAccountStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                // aria-label="Filter by account status"
                 aria-label={i18n.t("formActions.filterByAccountStatus")}
                disabled={filterOptionsLoading && accountStatusOptions.length === 0}
                className="h-11 w-full rounded-xl border border-[var(--color-text-border)] px-3 text-sm bg-white text-foreground focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10"
              >
                <option value=""><Trans i18nKey="crm.allAccountStatuses" /></option>
                {accountStatusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                className="text-sm font-semibold text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] transition whitespace-nowrap self-start lg:self-center"
                onClick={clearAllFilters}
              >
                {/* Clear Filters */}
                 <Trans i18nKey="formActions.clearFilters" />
              </button>
            )}
          </div>
        </div>

        {/* Listing Section */}
        {loading ? (
          <div className="bg-white rounded-2xl border border-white/60 p-8 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)]">
            <div className="flex flex-col gap-3">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="h-12 rounded-xl bg-[var(--color-workspace)]/50 animate-pulse" />
              ))}
            </div>
          </div>
        ) : customers.length > 0 ? (
          <div className="bg-white rounded-2xl border border-white/60 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] overflow-hidden">
            {/* Desktop Table View */}
            <DataTable columns={tableColumns} minWidth="720px">
              {customers.map((cust) => {
                const custId = cust.id || cust.customerId;
                const companyName = cust.companyName || cust.company_name || "—";
                const industryType = cust.industryType || cust.industry_type;
                const accountType = cust.accountType || cust.account_type || "Normal";
                const accountStatus = cust.accountStatus || cust.account_status || "Onboarding";
                const statusMeta = formatStatusDisplay(accountStatus);

                return (
                  <tr
                    key={custId}
                    className="hover:bg-[var(--color-workspace)]/30 cursor-pointer transition-colors"
                    onClick={() => navigate(CRM_ONBOARDING_VIEW_PATH(custId))}
                  >
                    <td className="px-4 py-3.5 font-semibold text-foreground">
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-[var(--color-primary)] shrink-0" />
                        <span>{companyName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-foreground">{industryType || <span className="text-[var(--color-text-secondary)]">—</span>}</td>
                    <td className="px-4 py-3.5">
                      <span className="inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                        {String(accountType).replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${statusMeta.className}`}>
                        {statusMeta.label}
                      </span>
                    </td>
                    {permissions.manageCustomerData && (
                      <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-2">
                          <Button
                            variant="ghost"
                            onClick={() => navigate(CRM_ONBOARDING_EDIT_PATH(custId))}
                            className="h-8 w-8 p-0 rounded-lg text-[var(--color-primary)] hover:bg-[var(--color-workspace)]"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {/* <Button
                            variant="ghost"
                            onClick={() => handleStartDelete(cust)}
                            className="h-8 w-8 p-0 rounded-lg text-[var(--color-error)] hover:bg-rose-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button> */}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </DataTable>

            {/* Mobile Cards View */}
            <div className="md:hidden divide-y divide-[var(--color-text-border)]/35">
              {customers.map((cust) => {
                const custId = cust.id || cust.customerId;
                const companyName = cust.companyName || cust.company_name || "—";
                const industryType = cust.industryType || cust.industry_type;
                const accountType = cust.accountType || cust.account_type || "Normal";
                const accountStatus = cust.accountStatus || cust.account_status || "Onboarding";
                const statusMeta = formatStatusDisplay(accountStatus);

                return (
                  <div
                    key={custId}
                    className="p-4 space-y-3 active:bg-[var(--color-workspace)]/40 transition"
                    onClick={() => navigate(CRM_ONBOARDING_VIEW_PATH(custId))}
                  >
                    <div className="flex justify-between items-start">
                      <h4 className="font-semibold text-foreground flex items-center gap-2">
                        <Building className="h-4 w-4 text-[var(--color-primary)] shrink-0" />
                        {companyName}
                      </h4>
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                        {String(accountType).replace(/_/g, " ")}
                      </span>
                    </div>

                    <div className="text-xs space-y-1 text-[var(--color-text-secondary)]">
                      <div className="flex justify-between">
                        {/* <span>Industry:</span> */}
                          <span><Trans i18nKey="crm.industry" /></span>
                        <span className="font-medium text-foreground">{industryType || "—"}</span>
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusMeta.className}`}>
                        {statusMeta.label}
                      </span>

                      {permissions.manageCustomerData && (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            onClick={() => navigate(CRM_ONBOARDING_EDIT_PATH(custId))}
                            className="h-8 w-8 p-0 rounded-lg text-[var(--color-primary)]"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => handleStartDelete(cust)}
                            className="h-8 w-8 p-0 rounded-lg text-[var(--color-error)]"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-t border-[var(--color-text-border)]/50">
                <Button
                  onClick={() => setCurrentPage(activePage - 1)}
                  disabled={activePage === 1}
                  className="h-9 px-3 rounded-lg border border-[var(--color-text-border)] text-xs hover:bg-[var(--color-workspace)] bg-white text-foreground"
                >
                  {/* &larr; Previous */}
                   <Trans i18nKey="pagination.previous" />
                </Button>
                <span className="text-xs text-[var(--color-text-secondary)]">
                  {/* Page {activePage} of {totalPages} ({totalElements} total) */}
                  <Trans i18nKey="pagination.page" /> {activePage} <Trans i18nKey="common.of" /> {totalPages} ({totalElements} <Trans i18nKey="crm.total" />
               
                </span>
                <Button
                  onClick={() => setCurrentPage(activePage + 1)}
                  disabled={activePage === totalPages}
                  className="h-9 px-3 rounded-lg border border-[var(--color-text-border)] text-xs hover:bg-[var(--color-workspace)] bg-white text-foreground"
                >
                  {/* Next &rarr; */}
                    <Trans i18nKey="pagination.next" />
                </Button>
              </div>
            )}
          </div>
        ) : (
          <EmptyState
            icon={Building}
            // title="No Customers Found"
             title={i18n.t("crm.noCustomersFound")}
            message="No customer records match your query or there are no accounts registered yet."
            action={
              permissions.createNewCustomer && (
                <Button
                  onClick={() => navigate(CRM_ONBOARDING_ADD_PATH)}
                  className="h-10 px-4 mt-4 rounded-xl bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)]"
                >
                  {/* Create First Customer */}
                  <Trans i18nKey="formActions.createFirstCustomer" />
                </Button>
              )
            }
          />
        )}

        {/* Delete Confirmation Modal Overlay */}
        {deleteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full border border-white/60 overflow-hidden animate-in zoom-in-95 duration-200">
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-3 text-[var(--color-error)] border-b border-[var(--color-text-border)]/40 pb-3">
                  <ShieldAlert className="h-6 w-6 shrink-0 text-rose-600" />
                  <h3 className="text-lg font-bold text-foreground">Delete Customer</h3>
                </div>

                {deleteError && (
                  <AlertBanner type="error">{deleteError}</AlertBanner>
                )}
                
                <div className="space-y-3 text-sm text-foreground">
                  <p className="font-semibold text-base">
                    {/* Are you sure you want to delete  */}
                    <Trans i18nKey="crm.areYouSureYouWantToDelete" /> 
                    <span className="text-rose-600 font-bold">&quot;{deleteTarget.name}&quot;</span>?
                  </p>
                  
                  <div className="p-4 rounded-xl bg-rose-50/80 border border-rose-200 text-rose-900 text-xs space-y-2">
                    <p className="font-bold text-rose-700">
                      {/* Deleting this customer will also permanently remove all associated: */}
                       <Trans i18nKey="crm.deletingThisCustomerWillAlsoPermanentlyRemoveAll" />
                    </p>
                    <ul className="list-disc list-inside space-y-1 font-medium text-rose-800">
                      {/* <li>Tax & Legal Information</li>
                      <li>Commercial Terms</li>
                      <li>Relationship Information</li>
                      <li>Contacts</li>
                      <li>Locations</li>
                      <li>Customer Assets</li> */}
                      <li><Trans i18nKey="crm.taxLegalInformation" /></li>
                      <li><Trans i18nKey="crm.commercialTerms" /></li>
                      <li><Trans i18nKey="crm.relationshipInformation" /></li>
                      <li><Trans i18nKey="crm.contacts" /></li>
                      <li><Trans i18nKey="crm.locations" /></li>
                      <li><Trans i18nKey="crm.customerAssets" /></li>
                    </ul>
                    <p className="font-bold text-rose-700 pt-1">
                      {/* This action cannot be undone. */}
                       <Trans i18nKey="validation.thisActionCannotBeUndone" />
                    </p>
                  </div>
                </div>

                <div className="flex justify-end gap-2.5 pt-3 border-t border-[var(--color-text-border)]/40">
                  <Button
                    onClick={() => {
                      setDeleteTarget(null);
                      setDeleteError(null);
                    }}
                    disabled={deleteLoading}
                    className="h-10 px-4 rounded-xl border border-[var(--color-text-border)] bg-white hover:bg-[var(--color-workspace)] text-foreground font-semibold text-sm"
                  >
                    {/* Cancel */}
                         <Trans i18nKey="formActions.cancel" />
                  </Button>
                  <Button
                    onClick={handleExecuteDelete}
                    disabled={deleteLoading}
                    className="h-10 px-5 rounded-xl bg-[var(--color-error)] text-white hover:bg-rose-700 font-semibold text-sm shadow-md cursor-pointer disabled:opacity-50"
                  >
                    {deleteLoading ? "Deleting..." : "Delete Customer"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
